import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiDel } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import { Mic, Plus, Star, Trash2, Voicemail as VoicemailIcon, PhoneCall, PhoneOutgoing } from 'lucide-react';
import toast from 'react-hot-toast';
import OutboundVoicemailList from '../components/phone/OutboundVoicemailList.jsx';
import LeadDetailsPane from '../components/leadDetail/LeadDetailsPane.jsx';

const TABS = [
  { id: 'drop', label: 'Voicemail Drops', icon: PhoneCall, desc: 'Pre-recorded messages dropped at the tone during an outbound call.' },
  { id: 'outbound', label: 'Outbound', icon: PhoneOutgoing, desc: 'Voicemails you left for leads. Click a name to open their full lead details.' },
  { id: 'box', label: 'Voicemail Box', icon: VoicemailIcon, desc: 'Greetings callers hear when they reach your voicemail box.' },
];

export default function Voicemail() {
  const [tab, setTab] = useState('drop');
  // Lead opened from an outbound voicemail. The list stays mounted beside it, so
  // audio that is playing is never interrupted.
  const [leadId, setLeadId] = useState(null);
  const outbound = tab === 'outbound';

  return (
    <div className="space-y-4">
      <TopBar title="Voicemail" subtitle="Recordings & Drops" />
      <div className="panel p-1 inline-flex gap-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-md text-sm flex items-center gap-2 transition ${
                tab === t.id ? 'bg-civix-purple/25 text-white border border-civix-purple/50' : 'text-white/60 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {outbound ? (
        <div className={`grid gap-4 ${leadId ? 'lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]' : ''}`}>
          {leadId && (
            // Left on large screens; a full-screen overlay on small ones.
            <div className="fixed inset-0 z-50 lg:static lg:z-auto lg:h-[calc(100vh-220px)] lg:min-h-[520px] lg:order-first rounded-xl overflow-hidden border border-civix-border">
              <LeadDetailsPane leadId={leadId} onClose={() => setLeadId(null)} />
            </div>
          )}
          <div className="space-y-3 min-w-0">
            <div className="text-sm text-white/60">{TABS.find((t) => t.id === 'outbound').desc}</div>
            <OutboundVoicemailList onOpenLead={setLeadId} />
          </div>
        </div>
      ) : (
        <RecordingsList kind={tab} />
      )}
    </div>
  );
}

function RecordingsList({ kind }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['voicemail', kind],
    queryFn: () => apiGet(`/voicemail?kind=${kind}`),
  });
  const items = data?.items || [];
  const desc = TABS.find((t) => t.id === kind)?.desc;

  return (
    <div className="space-y-4">
      <div className="text-sm text-white/60">{desc}</div>
      <NewRecordingForm kind={kind} onSaved={() => qc.invalidateQueries({ queryKey: ['voicemail', kind] })} />
      <div className="panel divide-y divide-white/5">
        {isLoading && <div className="p-6 text-white/50">Loading…</div>}
        {!isLoading && items.length === 0 && (
          <div className="p-6 text-white/50">No recordings yet. Add one above.</div>
        )}
        {items.map((r) => (
          <RecordingRow
            key={r._id}
            r={r}
            onChanged={() => qc.invalidateQueries({ queryKey: ['voicemail', kind] })}
          />
        ))}
      </div>
    </div>
  );
}

function NewRecordingForm({ kind, onSaved }) {
  const [name, setName] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [transcription, setTranscription] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!name.trim()) return toast.error('Name is required');
    setBusy(true);
    try {
      await apiPost('/voicemail', { name: name.trim(), kind, audioUrl: audioUrl.trim() || undefined, transcription: transcription.trim() || undefined });
      toast.success('Recording added');
      setName(''); setAudioUrl(''); setTranscription('');
      onSaved?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="panel p-4 space-y-3">
      <div className="font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> New {kind === 'drop' ? 'Voicemail Drop' : 'Voicemail Greeting'}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="label">Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={kind === 'drop' ? 'Quick callback request' : 'Default greeting'} />
        </div>
        <div>
          <label className="label">Audio URL (mp3/wav)</label>
          <input className="input" value={audioUrl} onChange={(e) => setAudioUrl(e.target.value)} placeholder="https://…/recording.mp3" />
        </div>
      </div>
      <div>
        <label className="label">Transcription (optional)</label>
        <textarea className="input" rows={2} value={transcription} onChange={(e) => setTranscription(e.target.value)} placeholder="Hi, this is..." />
      </div>
      <div className="flex justify-end">
        <button className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Add Recording'}</button>
      </div>
    </form>
  );
}

function RecordingRow({ r, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(r.name);
  const [audioUrl, setAudioUrl] = useState(r.audioUrl || '');
  const [transcription, setTranscription] = useState(r.transcription || '');
  const [busy, setBusy] = useState(false);

  async function setDefault() {
    setBusy(true);
    try {
      await apiPut(`/voicemail/${r._id}`, { isDefault: true });
      toast.success('Set as default');
      onChanged?.();
    } catch (err) { toast.error(err?.message || 'Failed'); }
    finally { setBusy(false); }
  }

  async function save() {
    setBusy(true);
    try {
      await apiPut(`/voicemail/${r._id}`, { name, audioUrl: audioUrl || undefined, transcription: transcription || undefined });
      toast.success('Saved');
      setEditing(false);
      onChanged?.();
    } catch (err) { toast.error(err?.message || 'Failed'); }
    finally { setBusy(false); }
  }

  async function remove() {
    if (!confirm(`Delete recording "${r.name}"?`)) return;
    setBusy(true);
    try {
      await apiDel(`/voicemail/${r._id}`);
      toast.success('Deleted');
      onChanged?.();
    } catch (err) { toast.error(err?.message || 'Failed'); }
    finally { setBusy(false); }
  }

  return (
    <div className="p-4">
      {!editing ? (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="font-medium flex items-center gap-2">
              <Mic className="w-4 h-4 text-civix-cyan" />
              <span className="truncate">{r.name}</span>
              {r.isDefault && (
                <span className="text-xs px-2 py-0.5 rounded-sm bg-civix-purple/30 text-white/90 border border-civix-purple/50 flex items-center gap-1">
                  <Star className="w-3 h-3" /> Default
                </span>
              )}
            </div>
            {r.transcription && <div className="text-xs text-white/50 mt-1 truncate">"{r.transcription}"</div>}
            {r.audioUrl && (
              <audio controls src={r.audioUrl} className="mt-2 max-w-md" />
            )}
          </div>
          <div className="flex items-center gap-2">
            {!r.isDefault && (
              <button className="btn-secondary text-xs" disabled={busy} onClick={setDefault}>
                <Star className="w-3 h-3 inline mr-1" /> Set default
              </button>
            )}
            <button className="btn-secondary text-xs" onClick={() => setEditing(true)}>Edit</button>
            <button className="btn-danger text-xs" onClick={remove} disabled={busy}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          <input className="input" value={audioUrl} onChange={(e) => setAudioUrl(e.target.value)} placeholder="Audio URL" />
          <textarea className="input" rows={2} value={transcription} onChange={(e) => setTranscription(e.target.value)} placeholder="Transcription" />
          <div className="flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
            <button className="btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
